import React, {useCallback, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import moment from 'moment'
import toaster from '../../../Utils/toaster'
import {CommonLoading} from '../../../Utils/commonLoading'
import type {BatchItem} from './bulkVosTypes'
import {formatBulkVosCallId} from './bulkVosClientPortalFormat'
import {fetchBulkVosVosFormView} from './bulkVosVosFormViewApi'
import {BulkVosVosFormFileModal} from './BulkVosVosFormFileModal'
import {DigitalVosFormModal} from '../appointment-management/components/digital-vos-form/DigitalVosFormModal'
import type {BuildDigitalVosFormInput} from '../appointment-management/components/digital-vos-form/digitalVosFormTypes'
import {fetchDigitalVosFormPayload} from '../appointment-management/components/digital-vos-form/fetchDigitalVosFormPayload'
import {resolveBulkVosAppointmentCode} from './bulkVosOsiEditUtils'

const UPLOADS_BASE = process.env.REACT_APP_IMG_URL

type Props = {
  item: BatchItem
  portalToken?: string
  className?: string
}

function resolveLegacyFileUrl(
  fileName: string,
  osiSubmissionCode: number,
): string {
  if (fileName.startsWith('s3://')) return fileName
  return `${UPLOADS_BASE}/files/${osiSubmissionCode}/${fileName}`
}

export function BulkVosVosFormViewIcon({item, portalToken, className}: Props) {
  const [loading, setLoading] = useState(false)
  const loadingRef = useRef(false)
  const [fileModal, setFileModal] = useState<{url: string; fileName: string} | null>(null)
  const [digitalModal, setDigitalModal] = useState<{
    input: BuildDigitalVosFormInput
    fileName: string
  } | null>(null)

  const openView = useCallback(async () => {
    if (loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    try {
      const view = await fetchBulkVosVosFormView(item.id, portalToken)
      const labelCall = formatBulkVosCallId(item.callId) || String(view.appointmentCode)
      const downloadName = `VOS Form ${labelCall}-${moment().format('MM/DD/YYYY')}.pdf`

      if (view.viewUrl?.trim()) {
        setFileModal({url: view.viewUrl.trim(), fileName: view.fileName ?? downloadName})
        return
      }

      if (view.openDigitalViewer) {
        if (portalToken) {
          toaster('error', 'Digital VOS preview requires a signed-in session or an uploaded VOS file.')
          return
        }
        const appointmentCode = view.appointmentCode || resolveBulkVosAppointmentCode(item)
        const digital = await fetchDigitalVosFormPayload(appointmentCode, {requireApproved: false})
        if (!digital.ok) {
          toaster('error', digital.message)
          return
        }
        setDigitalModal({input: digital.input, fileName: downloadName})
        return
      }

      if (view.fileName?.trim()) {
        setFileModal({
          url: resolveLegacyFileUrl(view.fileName.trim(), view.osiSubmissionCode),
          fileName: view.fileName.trim(),
        })
        return
      }

      toaster('error', 'No VOS form is available for this record')
    } catch (e) {
      toaster('error', e instanceof Error ? e.message : 'Unable to load VOS form')
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [item.id, item.callId, portalToken])

  return (
    <>
      {loading && createPortal(<CommonLoading />, document.body)}

      <button
        type='button'
        className={className ?? 'bvp-vos-doc-btn'}
        aria-label='View VOS form'
        title='View VOS form'
        disabled={loading}
        onClick={() => void openView()}
      >
        <i className={`bi bi-file-earmark-text${loading ? ' opacity-50' : ''}`} aria-hidden />
      </button>

      <BulkVosVosFormFileModal
        show={fileModal != null}
        onClose={() => setFileModal(null)}
        fileName={fileModal?.fileName ?? 'VOS form.pdf'}
        viewUrl={fileModal?.url ?? ''}
      />

      {digitalModal && (
        <DigitalVosFormModal
          show
          layout='popup'
          onClose={() => setDigitalModal(null)}
          fileName={digitalModal.fileName}
          input={digitalModal.input}
        />
      )}
    </>
  )
}
